import {
  IBillingBalance,
  IBillingTransactions,
  IPaymentInfo,
  IPromiseOfPayment,
  IPaymentDataResponse,
  IBillCategoryDetailsResponse,
  IOdbBillCoverResponse,
  IOdbBillCategoryDetailsResponse,
} from '@dch/billing-business-logic';
import { IEntity, DATASTRATEGY } from '@dch/entity-manager';
import { SessionStorageStrategy } from '@dch/entity-manager/lib/storage/strategies/session-storage.strategy';
import { getSmoothAccessRequiredHeaders } from '../services/utils.service';
import { IOdbUsageLevel4Response } from '../models/odb-usage-level-4.model';
import { IOdbBillAggregatorResponse } from '../models/aggregator.model';
import { IBillingTransactionsReferences } from '../models/billing-transactions-references.model';

export const BILLING_TRANSACTIONS_ENTITY: IEntity<IBillingTransactions> = {
  requestHeaders: ['language'],
  secure: true,
  version: 'v1',
  rel: 'blt:billingTransaction',
  url: '/rest/billing-transactions/customer/{cdbId}/billingAccounts/fls/{billingAccountNumber}/transactions?instalmentPlanExtension=true',
  dataStrategy: DATASTRATEGY.CACHE_NETWORK_WITH_ERRORS,
  storageStrategy: SessionStorageStrategy.ID,
  requiredJwts: getSmoothAccessRequiredHeaders(),
};

export const BILLING_TRANSACTIONS_REFERENCES_ENTITY: IEntity<IBillingTransactionsReferences> =
  {
    requestHeaders: ['language'],
    secure: true,
    rel: 'bt:paTransactionsReferences',
    url: '/rest/billing-transactions/customer/{cdbId}/billingAccounts/fls/{paNumber}/transactions/references?refreshCache=false&filterSplitInvoice=true',
    version: 'v1',
    dataStrategy: DATASTRATEGY.CACHE_NETWORK_WITH_ERRORS,
    storageStrategy: SessionStorageStrategy.ID,
    requiredJwts: getSmoothAccessRequiredHeaders(),
  };

export const BILLING_BALANCE_ENTITY: IEntity<IBillingBalance> = {
  requestHeaders: ['encryptionKey', 'language'],
  secure: true,
  version: 'v1',
  rel: 'bla:billingBalance',
  url: '/rest/billing-accounts/customer/{cdbId}/balances?filterActiveAccount=true&refreshCache=false',
  dataStrategy: DATASTRATEGY.CACHE_NETWORK_WITH_ERRORS,
  storageStrategy: SessionStorageStrategy.ID,
  requiredJwts: getSmoothAccessRequiredHeaders(),
};

export const PAYMENT_INFO_ENTITY: IEntity<IPaymentInfo> = {
  requestHeaders: ['language'],
  secure: true,
  version: 'v1',
  rel: 'blp:paymentInfo',
  url: '/rest/billing-payments/customer/{cdbId}/billingAccount/fls/{billingAccountNumber}/paymentInfo',
  dataStrategy: DATASTRATEGY.CACHE_NETWORK_WITH_ERRORS,
  storageStrategy: SessionStorageStrategy.ID,
  requiredJwts: getSmoothAccessRequiredHeaders(),
};

export const PAYMENT_DATA_ENTITY: IEntity<IPaymentDataResponse> = {
  requestHeaders: ['language'],
  version: 'v1',
  rel: 'bp:papaymentdata',
  url: '/rest/billing-payments/customer/{cdbId}/billingAccount/fls/{billingAccountNumber}/paymentData',
  dataStrategy: DATASTRATEGY.PROXY_STORAGE,
  storageStrategy: SessionStorageStrategy.ID,
  timeout: 20000,
  requiredJwts: getSmoothAccessRequiredHeaders(),
};

export const UPDATE_BILLING_PAYMENT_ENTITY: IEntity<{}> = {
  requestHeaders: ['language'],
  version: 'v1',
  rel: 'bp:paupdatebillingpayment',
  url: '/rest/billing-payments/customer/{cdbId}/billingAccount/fls/{billingAccountNumber}/updateBillingPayment',
  dataStrategy: DATASTRATEGY.PROXY_STORAGE,
  storageStrategy: SessionStorageStrategy.ID,
  requiredJwts: getSmoothAccessRequiredHeaders(),
  timeout: 20000,
};

export const PROMISE_OF_PAYMENT_ENTITY: IEntity<IPromiseOfPayment> = {
  requestHeaders: ['language'],
  secure: true,
  version: 'v1',
  rel: 'blp:promiseOfPayment',
  url: '/rest/billing-pop/customer/{cdbId}/fls/{billingAccountNumber}/promiseOfPayment',
  dataStrategy: DATASTRATEGY.CACHE_NETWORK_WITH_ERRORS,
  storageStrategy: SessionStorageStrategy.ID,
  requiredJwts: getSmoothAccessRequiredHeaders(),
};

export const BILL_DOWNLOADER_ENTITY: IEntity<ArrayBuffer> & {
  responseType: string;
} = {
  requestHeaders: ['language'],
  secure: true,
  rel: 'bd:flsbillingdocument',
  url: '/rest/bill-downloader/customer/{cdbId}/billingAccount/fls/{paId}/billingDocument/{documentId}?documentDate={documentDate}{&type}',
  version: 'v1',
  dataStrategy: DATASTRATEGY.PROXY_STORAGE,
  storageStrategy: SessionStorageStrategy.ID,
  disableCache: true,
  responseType: 'arraybuffer',
  requiredJwts: getSmoothAccessRequiredHeaders(),
};

export const NEW_BILL_DOWNLOADER_ENTITY: IEntity<ArrayBuffer> & {
  responseType: string;
} = {
  requestHeaders: ['language'],
  secure: true,
  rel: 'bd:billingdocument',
  url: '/rest/bill-downloader/billingAccount/fls/billingDocument/{documentId}/{docType}',
  version: 'v2',
  dataStrategy: DATASTRATEGY.PROXY_STORAGE,
  storageStrategy: SessionStorageStrategy.ID,
  disableCache: true,
  responseType: 'arraybuffer',
  requiredJwts: getSmoothAccessRequiredHeaders(),
};

export const BILL_CATEGORY_DETAILS_ENTITY: IEntity<IBillCategoryDetailsResponse> =
  {
    requestHeaders: ['language'],
    secure: true,
    rel: 'bd:categorydetails',
    version: 'v1',
    url: '/rest/billing-details/customer/{cdbId}/billingAccount/{paNumber}/reference/{reference}/category/{category}/details',
    dataStrategy: DATASTRATEGY.CACHE_NETWORK_WITH_ERRORS,
    storageStrategy: SessionStorageStrategy.ID,
    requiredJwts: getSmoothAccessRequiredHeaders(),
  };

export const BILLING_COVER_ENTITY: IEntity<IOdbBillCoverResponse> = {
  version: 'v1',
  rel: 'bd:cover',
  url: '/rest/billing-details/customer/{cdbId}/billingAccount/{billingAccountNumber}/reference/{reference}/cover',
  dataStrategy: DATASTRATEGY.CACHE_NETWORK_WITH_ERRORS,
  storageStrategy: SessionStorageStrategy.ID,
  requiredJwts: getSmoothAccessRequiredHeaders(),
};

export const ODB_BILL_CATEGORY_DETAILS_ENTITY: IEntity<IOdbBillCategoryDetailsResponse> =
  {
    requestHeaders: ['language', 'proximusContentType', 'customHeaders'],
    secure: true,
    rel: 'bd:categorydetails',
    version: 'v3',
    url: '/rest/billing-details/customer/{cdbId}/billingAccount/{billingAccountNumber}/reference/{documentReference}/category/{category}/details',
    dataStrategy: DATASTRATEGY.CACHE_NETWORK_WITH_ERRORS,
    storageStrategy: SessionStorageStrategy.ID,
    requiredJwts: getSmoothAccessRequiredHeaders(),
  };

export const DEFERRAL_DATE_ENTITY: IEntity<{}> = {
  version: 'v1',
  rel: 'bf:deferral',
  url: '/rest/billing-facilities/customer/{cdbid}/fls/{paNumber}/invoice/{invoiceReference}/deferral',
  dataStrategy: DATASTRATEGY.PROXY_STORAGE,
  storageStrategy: SessionStorageStrategy.ID,
  timeout: 20000,
  requiredJwts: getSmoothAccessRequiredHeaders(),
};

export const ODB_USAGE_LEVEL_4_ENTITY: IEntity<IOdbUsageLevel4Response> = {
  version: 'v1',
  rel: 'bd:usageLevel4',
  url: '/rest/billing-details/customer/{cdbId}/reference/{reference}/cdrDetails?billableGroupIdentifier={billableGroupIdentifier}',
  dataStrategy: DATASTRATEGY.CACHE_NETWORK_WITH_ERRORS,
  storageStrategy: SessionStorageStrategy.ID,
  timeout: 20000,
  requiredJwts: getSmoothAccessRequiredHeaders(),
};

export const BILLING_AGGREGATOR_ENTITY: IEntity<IOdbBillAggregatorResponse> = {
  version: 'v1',
  rel: 'bag:evolution',
  url: '/rest/billing-aggregator/customer/{cdbId}/billingAccounts/evolution',
  dataStrategy: DATASTRATEGY.CACHE_NETWORK_WITH_ERRORS,
  storageStrategy: SessionStorageStrategy.ID,
  timeout: 20000,
  requiredJwts: getSmoothAccessRequiredHeaders(),
};

export const EXTRA_COST_ENTITY: IEntity<string> & { responseType: string } = {
  requestHeaders: ['language'],
  rel: 'mag:extraCost',
  url: '/dam/cdn/static/webResources/myBill/extraCost.csv',
  version: 'v1',
  dataStrategy: DATASTRATEGY.CACHE_NETWORK_WITH_ERRORS,
  storageStrategy: SessionStorageStrategy.ID,
  // Note: used to avoid the response to be parsed as JSON by EM
  responseType: 'text',
};
